import React from 'react'
import { Package, ShoppingBag, Users, BarChart3, DollarSign, RefreshCw } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import Currency from '../Currency'
import { getFeaturedImageUrl, getProductImageUrl } from '../../utils/imageUtils'

const DashboardTab = ({ adminStats, adminStatsLoading, onRefreshStats }) => {
  // Use real data from adminStats or fallback to 0
  const stats = [
    { 
      name: 'Total Products', 
      value: adminStats?.totalProducts || 0, 
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      type: 'number'
    },
    { 
      name: 'Active Orders', 
      value: adminStats?.activeOrders || 0, 
      icon: ShoppingBag,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      type: 'number'
    },
    { 
      name: 'Total Users', 
      value: adminStats?.totalUsers || 0, 
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      type: 'number'
    },
    { 
      name: 'Total Orders', 
      value: adminStats?.totalOrders || 0, 
      icon: BarChart3,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      type: 'number'
    },
    { 
      name: 'Revenue', 
      value: adminStats?.totalRevenue || 0, 
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      type: 'currency'
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Last update: {new Date().toLocaleString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>
        </div>
        <button
          onClick={onRefreshStats}
          disabled={adminStatsLoading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${adminStatsLoading ? 'animate-spin' : ''}`} />
          {adminStatsLoading ? 'Updating...' : 'Update Data'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {adminStatsLoading ? (
          // Loading skeleton for stats cards
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="bg-white shadow rounded-lg border animate-pulse">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-6 w-6 bg-gray-200 rounded" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-6 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          stats.map((stat) => (
            <div key={stat.name} className={`${stat.bgColor} shadow rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200`}>
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                      <dd className={`text-lg font-bold ${stat.color}`}>
                        {stat.type === 'currency' ? (
                          <Currency amount={stat.value} />
                        ) : (
                          stat.value
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Products */}
        <div className="bg-white shadow rounded-lg border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">Recent Products</h2>
          </div>
          <div className="px-6 py-4">
            {adminStatsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-3">
                {adminStats?.recentProducts?.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                          {getProductImageUrl(product) ? (
                            <img
                              src={getProductImageUrl(product)}
                              alt={product.name}
                              className="w-full h-full object-cover rounded-lg"
                            />
                          ) : (
                            <Package className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.category_name || 'No category'}</p>
                        <p className="text-xs text-gray-500"><Currency amount={product.price} /></p>
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        product.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {product.status === 'published' ? 'منشور' : 'مسودة'}
                      </span>
                    </div>
                  </div>
                ))}
                {!adminStats?.recentProducts?.length && (
                  <p className="text-gray-500 text-center py-4">لا توجد منتجات حديثة</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white shadow rounded-lg border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">الطلبات الحديثة</h2>
          </div>
          <div className="px-6 py-4">
            {adminStatsLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-3">
                {adminStats?.recentOrders?.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">#{order.order_number}</p>
                      <p className="text-xs text-gray-500">
                        {order.first_name} {order.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('ar-EG')}
                      </p>
                    </div>
                    <div className="text-sm text-right">
                      <p className="font-medium text-gray-900"><Currency amount={order.total_amount} /></p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                            style={{ backgroundColor: order.status_color + '20', color: order.status_color }}>
                        {order.status || 'غير محدد'}
                      </span>
                    </div>
                  </div>
                ))}
                {!adminStats?.recentOrders?.length && (
                  <p className="text-gray-500 text-center py-4">لا توجد طلبات حديثة</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardTab
